/**
 * Button — web implementation using standard Pressable.
 *
 * Web-specific styles (cursor, transition) are applied via Platform.select
 * where possible, or inline with explicit ViewStyle typing.
 *
 * Focus is rendered with a CSS outline so it never affects layout (no height
 * growth, no radius mismatch). The outline follows the button's own
 * border-radius automatically.
 */
import { Pressable, type ViewStyle } from "react-native";
import { type FC, memo, useMemo } from "react";
import { ButtonContent, type ButtonProps, useButtonSharedState } from "./shared";

const FOCUS_OUTLINE_WIDTH = 2;
const FOCUS_OUTLINE_OFFSET = 2;

const Button: FC<ButtonProps> = memo(function Button(props) {
  const state = useButtonSharedState(props);

  // Web-specific interactive styles. `cursor` is in React Native's ViewStyle
  // but limited to "auto" | "pointer". Properties like `transition`,
  // `userSelect`, and `WebkitTapHighlightColor` are accepted by
  // react-native-web at runtime but not in the RN type system. We merge them
  // via Object.assign to avoid a widening `as ViewStyle` cast.
  const webInteractiveStyle = useMemo(
    () =>
      Object.assign(
        {
          cursor: state.disabled || state.loading ? "auto" : "pointer",
          outlineWidth: 0,
        } satisfies ViewStyle,
        {
          transition: "all 0.2s ease-in-out",
          userSelect: "none",
          WebkitTapHighlightColor: "transparent",
        },
      ),
    [state.disabled, state.loading],
  );

  const focusOutlineStyle = useMemo(
    (): ViewStyle | undefined =>
      state.isFocused ?
        {
          outlineStyle: "solid",
          outlineWidth: FOCUS_OUTLINE_WIDTH,
          outlineOffset: FOCUS_OUTLINE_OFFSET,
          outlineColor: state.focusBorderColor,
        }
      : undefined,
    [state.isFocused, state.focusBorderColor],
  );

  return (
    <Pressable
      onPress={state.onPress}
      disabled={state.disabled || state.loading}
      style={({ pressed }) => [
        ...state.baseStyles,
        webInteractiveStyle,
        focusOutlineStyle,
        pressed && !state.disabled && !state.loading ? { opacity: 0.9 } : undefined,
      ]}
      onFocus={() => {
        state.setIsFocused(true);
      }}
      onBlur={() => {
        state.setIsFocused(false);
      }}
      accessibilityRole="button"
      accessibilityState={{
        disabled: state.disabled || state.loading,
        busy: state.loading,
      }}
      accessibilityLabel={
        state.accessibilityLabel ??
        (state.showLoadingText !== false && state.showLoadingText !== undefined ? state.loadingText : state.label)
      }
      testID={state.testID}
    >
      <ButtonContent
        iconLeft={state.iconLeft}
        iconRight={state.iconRight}
        showChildren={state.showChildren}
        showSpinner={state.showSpinner}
        loadingText={state.loadingText}
        textStyles={state.textStyles}
        textStyle={state.textStyle}
        textColor={state.textColor}
        uppercase={state.uppercase}
        label={state.label}
        themeSpacingSm={state.themeSpacingSm}
        showLoadingText={state.showLoadingText}
      />
    </Pressable>
  );
});

export { Button };
export type { ButtonProps };
